export const METERS_TO_FEET = 3.2808399;
export const MILES_TO_FEET = 5280;
export const METERS_TO_MIlES = METERS_TO_FEET / MILES_TO_FEET;
export const SI_KILO = 1000;
// export const SI_CENTI = 0.01;
export const MINUTE = 60;
export const HOUR = 60 * MINUTE;

const Scales = {
  distance: {
    metric: [
      { unit: 'km', unitPlural: 'km', threshold: SI_KILO },
      { unit: 'm', unitPlural: 'm', threshold: 1 },
      // { unit: 'cm', unitPlural: 'cm', threshold: SI_CENTI },
    ],
    imperial: [
      { unit: 'mile', unitPlural: 'mi', threshold: MILES_TO_FEET },
      { unit: 'ft', unitPlural: 'ft', threshold: 1 },
    ],
  },
};

/**
 * Returns the best fitting scale for the given value. Default to last scale.
 * @param {number} value - an integer or float
 * @param {object[]} scales — list of scale objects ordered in descending precision
 */
function unitBestScale (value, scales) {
  var scale;
  for (const s of scales) {
    scale = s;
    if (value >= scale.threshold) break;
  }
  return {
    value: value / scale.threshold,
    unit: Math.abs(value) === 1 ? scale.unit : scale.unitPlural,
  };
}

// This function does the same work than unitBestScale(),
// but we just want to use the classical units : meter or feet.
// We want to use this function only for certain KPIS where
// km and miles units are irrelevant (ie Altitude, Elevation +/-)
function unitScale (value, scale) {
  return {
    value: value / scale.threshold,
    unit: Math.abs(value) === 1 ? scale.unit : scale.unitPlural,
  };
}

// This function does the same work than formatDistance(),
// but we just want to use the classical units : meter or feet.
// We want to use this function only for certain KPIS where
// km and miles units are irrelevant (ie Altitude, Elevation +/-)
export function formatAltitude (distance) {
  if (isNaN(distance)) return "-";
  const { value, unit } = usingMetricUnits()
    ? unitScale(distance, Scales.distance.metric[1])
    : unitScale(distance * METERS_TO_FEET, Scales.distance.imperial[1]);
  return `${value.toFixed(0)} ${unit}`;
}

/**
 * Returns whether the app is currently set to use metric units (not imperial).
 */
export function usingMetricUnits () {
  const unit = process.client ? $nuxt.$cookies.get('unit') ?? 'metric' : 'metric';
  return unit === 'metric';
}

/**
 * Format a speed value in "distance per hour" using most appropriate distance scale.
 * @param {number} speed - speed in meters per second
 */
export function formatSpeed (speed) {
  if (isNaN(speed)) return "-";
  const { value, unit } = usingMetricUnits()
    ? unitBestScale(speed * HOUR, Scales.distance.metric)
    : unitBestScale(speed * METERS_TO_FEET * HOUR, Scales.distance.imperial);
  return `${(value).toFixed(2)} ${unit}/h`;
}

export function formatTemperature (temperature) {
  if (isNaN(temperature)) return "-";
  return usingMetricUnits()
    ? `${temperature} °C`
    : `${(temperature * (9 / 5) + 32).toFixed(1)} °F`;
}

/**
 * Format an allure value in "minutes per distance unit" depending on current unit system.
 * @param {number} allure - allure in seconds per meters
 */
export function formatAllure (allure) {
  if (isNaN(allure)) return "-";
  return usingMetricUnits()
    ? `${(allure / MINUTE * SI_KILO).toFixed(2)} min/km`
    : `${(allure / MINUTE / METERS_TO_MIlES).toFixed(2)} min/mi`;
}

/**
 * Format a distance value using most appropriate distance scale.
 * @param {number} distance - distance in meters
 */
export function formatDistance (distance) {
  if (isNaN(distance)) return "-";
  const { value, unit } = usingMetricUnits()
    ? unitBestScale(distance, Scales.distance.metric)
    : unitBestScale(distance * METERS_TO_FEET, Scales.distance.imperial);
  return `${value.toFixed(2)} ${unit}`;
}
